
import { Component, Mixins } from 'vue-property-decorator';
import { Auth } from 'aws-amplify';
import { AmplifyEventBus } from 'aws-amplify-vue';
import { I18n } from '@aws-amplify/core';
import { CognitoUserInterface } from '@aws-amplify/ui-components';
import { Profile } from '@/api/model';
import MessageToastMixin from '@/mixins/MessageToastMixin';
import FormGroup from '@/components/profile/FormGroup.vue';
import LanguageLabel from '@/components/profile/LanguageLabel.vue';
import MfaInfo from '@/components/profile/MfaInfo.vue';
import AmplifyI18n from '@/components/authentication/AmplifyI18n';
import AuthGroup from '@/logic/auth/AuthGroup';

const cognitoFormGroups = ['email', 'password'];

@Component({
  components: { MfaInfo, FormGroup, LanguageLabel },
})
export default class ProfileForm extends Mixins(MessageToastMixin) {
  formGroups: any = {
    profile: {
      active: false,
      fields: [
        { name: 'firstName' },
        { name: 'lastName' },
        { name: 'functionTitle' },
        { name: 'city' },
        { name: 'country', type: 'country' },
      ],
    },
    email: {
      active: false,
      fields: [{ name: 'email', type: 'email' }],
    },
    password: {
      active: false,
      fields: [
        { name: 'oldPassword', type: 'password' },
        { name: 'newPassword', type: 'password' },
        { name: 'newPasswordRepeat', type: 'password' },
      ],
    },
    locale: {
      active: false,
      fields: [{ name: 'locale', type: 'locale' }],
    },
    phone: {
      active: false,
      fields: [{ name: 'phone', type: 'tel' }],
    },
    mfa: {
      active: false,
      fields: [{ name: 'mfa' }],
    },
  };

  loading = false;

  get canEditProfile(): boolean {
    return !this.$store.getters['auth/memberOf'](AuthGroup.Multinational);
  }

  get profile(): Profile {
    return this.$store.state.profile.profile;
  }

  get fullName(): string {
    return this.$store.getters['profile/fullName'];
  }

  get user(): CognitoUserInterface {
    return this.$store.state.auth.user;
  }

  get hasPosessionOfLanguage(): boolean {
    return this.$store.getters['auth/memberOf'](AuthGroup.Multinational);
  }

  get mfaConfig(): any {
    const mfaTypes = ['TOTP', 'None'];
    if (
      this.user &&
      this.user.attributes &&
      this.user.attributes.phone_number
    ) {
      mfaTypes.push('SMS');
    }
    return {
      mfaDescription: this.$t('profile.mfa.mfa-description'),
      mfaTypes,
      tokenInstructions: this.$t('profile.mfa.token-instructions'),
      smsDescription: this.$t('profile.mfa.sms-description'),
      totpDescription: this.$t('profile.mfa.totp-description'),
      noMfaDescription: this.$t('profile.mfa.no-mfa-description'),
      cancelHandler: () => {
        this.cancel('mfa');
      },
    };
  }

  get preferredMFA(): string {
    return this.user.preferredMFA;
  }

  async beforeCreate() {
    await this.$store.dispatch('auth/getUser');
    AmplifyEventBus.$on('authState', this.onAuthStateChange);

    I18n.putVocabularies(AmplifyI18n);
  }

  async onAuthStateChange(authState: string) {
    if (authState === 'signedIn' && this.formGroups.mfa.active) {
      this.$store.dispatch('auth/disableMFAOnboarding');
      this.showSuccess(this.$t('profile.error.success'));
      this.cancel('mfa');
    }
  }

  edit(formGroup: string) {
    this.formGroups[formGroup].active = true;
    Object.keys(this.formGroups)
      .filter((group) => group !== formGroup)
      .forEach((group) => {
        this.formGroups[group].active = false;
      });
  }

  cancel(formGroup: string) {
    this.formGroups[formGroup].active = false;
  }

  formGroupHasData(formGroup: string): boolean {
    return this.formGroups[formGroup].fields.some(
      // @ts-ignore
      (field) => this.profile[field.name],
    );
  }

  async save(formGroup: string) {
    const group = this.formGroups[formGroup];
    const newFields: any = {};
    group.fields.forEach((f: any) => {
      if (f.value !== undefined) {
        newFields[f.name] = f.value;
      }
    });
    const newProfile = Object.assign(this.profile, newFields);

    // Sync to cognito
    if (cognitoFormGroups.includes(formGroup)) {
      const user = await Auth.currentAuthenticatedUser();

      try {
        // Password
        if (formGroup === 'password') {
          const success = await this.savePassword(newFields, user);
          if (!success) {
            return;
          }
        } else {
          this.saveAttributes(newFields, user, formGroup);
        }
      } catch (e) {
        this.showError(this.$t('profile.password.invalid'));
        return;
      }
    }

    // Sync to aws database
    try {
      if (formGroup !== 'password') {
        this.loading = true;
        await this.$store.dispatch('profile/saveProfile', newProfile);
        this.loading = false;
      }
      group.active = false;
      this.showSuccess(this.$t('profile.error.success'));
    } catch (e) {
      this.showError(this.$t('profile.error.failed'));
    }
  }

  setField(formGroup: string, fieldName: string, value: string) {
    const field = this.formGroups[formGroup].fields.find(
      (f: any) => f.name === fieldName,
    );
    field.value = value;
  }

  async savePassword(fields: any, user: any): Promise<boolean> {
    if (fields.newPassword !== fields.newPasswordRepeat) {
      this.showError(this.$t('profile.password.do-not-match'));
      return false;
    }
    await Auth.changePassword(user, fields.oldPassword, fields.newPassword);
    this.showSuccess(this.$t('profile.password.change-success'));
    return true;
  }

  async saveAttributes(
    fields: any,
    user: CognitoUserInterface,
    formGroup: string,
  ) {
    const { attributes } = user;

    // Only sync existing fields
    const newAttributes: any = {};
    if (fields.email && attributes.email !== fields.email) {
      newAttributes.email = fields.email;
    }
    if (
      fields.phone !== undefined &&
      attributes.phone_number !== fields.phone
    ) {
      newAttributes.phone_number = fields.phone;
    }
    if (fields.locale && attributes.locale !== fields.locale) {
      newAttributes.locale = fields.locale;
    }
    try {
      await Auth.updateUserAttributes(user, newAttributes);
    } catch (e: any) {
      if (e.code === 'InvalidParameterException') {
        this.showError(this.$t('profile.phone.invalid-phone-number'));
      } else {
        this.showError(this.$t('profile.phone.unknown-error'));
      }
    }

    if (formGroup === 'email') {
      this.showEmailConfirm();
    }

    if (formGroup === 'locale') {
      this.showSuccess(this.$t('profile.locale.change-success'));
    }
  }

  showEmailConfirm() {
    this.$buefy.dialog.prompt({
      message: this.$t('profile.email.confirmation-code') as string,
      inputAttrs: {
        type: 'text',
        placeholder: this.$t('profile.placeholder.confirmation-code'),
        value: '',
      },
      confirmText: this.$t('profile.label.save') as string,
      canCancel: false,
      trapFocus: true,
      closeOnConfirm: false,
      onConfirm: (code, { close }) => this.confirmEmail(code, close),
    });
  }

  // If email changed, show input popup message for confirmation mail code
  async confirmEmail(code: string, close: () => void) {
    try {
      await Auth.verifyCurrentUserAttributeSubmit('email', code);
      this.showSuccess(this.$t('profile.email.confirmed'));
      close();
    } catch (e) {
      this.showError(this.$t('profile.email.error-confirm-code'));
    }
  }

  isEditable(formGroup: string): boolean {
    if (
      (cognitoFormGroups.includes(formGroup) || formGroup === 'mfa') &&
      this.$store.getters['auth/isImpersonating']
    ) {
      return false;
    }
    return true;
  }
}
